import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Button, Col, Collapse, Form, Row, Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import MaskedInput from 'react-text-mask';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBuilding,
  faCheck,
  faCheckCircle,
  faChevronDown,
  faCity,
  faEnvelope,
  faHashtag,
  faMapMarkedAlt,
  faMapMarkerAlt,
  faPaperPlane,
  faPhoneAlt,
  faRoad,
  faSitemap,
  faUser,
  faUserTag
} from '@fortawesome/free-solid-svg-icons';
import IconAlert from 'components/common/IconAlert';
import useRequestState from 'hooks/useRequestState';
import {
  NETWORK_ERROR_MESSAGE,
  checkRegistrationDomain,
  getApiErrorMessage,
  submitRegistration
} from 'helpers/registrationApi';
import {
  PHONE_MASK,
  STATE_OPTIONS,
  digitsOnly,
  isValidCity,
  isValidEmail,
  isValidMobile,
  isValidName,
  isValidState,
  isValidZip
} from 'components/admin/facility-staff/add-facility-staff/staffFormRules';
import { RegisterHeroIllustration } from 'components/registration-shared/RegistrationIllustrations';
import { ToneIcon } from 'components/registration-shared/RegistrationUI';
import PRODUCT_FEATURES from 'components/registration-shared/productFeatures';
import RegistrationLayout from './registration/RegistrationLayout';
import RegistrationSuccessPanel from './registration/RegistrationSuccessPanel';

/**
 * Public self-registration form — /register (userregistrations.md U2).
 *
 * A NEW form on purpose (D1): the Falcon RegistrationForm.js template is left
 * untouched. Single page (D3), no password field (D4) — the applicant chooses a
 * password from the welcome email after an admin approves them (U5).
 *
 * Organization, role, facilities and username are deliberately NOT asked here:
 * client organizations and facilities must not be listed on a public page. The
 * reviewing administrator sets them all when approving the request.
 *
 * The one lookup is POST /users/registration-domain-check: when the email's
 * domain isn't one of our clients (or the check fails), the applicant is asked
 * for their agency's name instead. Nothing the check returns is shown.
 *
 * Copy is deliberately short: the left panel promotes product features
 * instead of repeating the steps, and the form only says what the applicant
 * needs to know.
 */

const EMPTY_FORM = {
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  organization_name: '',
  address: '',
  address2: '',
  city: '',
  state: '',
  zip: '',
  website: '' // honeypot (backend R6.2) — stays '' for humans
};

// Order used to find (and focus) the first invalid field on submit.
const FIELD_ORDER = [
  'first_name',
  'last_name',
  'email',
  'phone',
  'organization_name',
  'address',
  'city',
  'state',
  'zip'
];

const ADDRESS_FIELDS = ['address', 'address2', 'city', 'state', 'zip'];

const ORGANIZATION_NAME_MAX = 100;

const getEmailDomain = email => {
  const value = String(email || '').trim();
  return isValidEmail(value) ? value.split('@').pop().toLowerCase() : '';
};

// Domain-check outcomes that mean "ask for the agency's name".
const ASK_ORGANIZATION = ['other', 'error'];

const validate = (values, { needsOrganization = false } = {}) => {
  const errors = {};
  const trimmed = name => String(values[name] || '').trim();

  if (!trimmed('first_name')) {
    errors.first_name = 'Enter your first name.';
  } else if (!isValidName(values.first_name)) {
    errors.first_name = 'Use letters only.';
  }

  if (!trimmed('last_name')) {
    errors.last_name = 'Enter your last name.';
  } else if (!isValidName(values.last_name)) {
    errors.last_name = 'Use letters only.';
  }

  if (!trimmed('email')) {
    errors.email = 'Enter your work email.';
  } else if (!isValidEmail(values.email)) {
    errors.email = 'Enter a valid email address.';
  }

  if (!digitsOnly(values.phone)) {
    errors.phone = 'Enter your phone number.';
  } else if (!isValidMobile(values.phone)) {
    errors.phone = 'Enter a 10-digit phone number.';
  }

  if (needsOrganization) {
    const organization = trimmed('organization_name');
    if (!organization) {
      errors.organization_name = "Enter your agency's name.";
    } else if (organization.length < 2) {
      errors.organization_name = "Enter your agency's full name.";
    }
  }

  if (trimmed('city') && !isValidCity(values.city)) {
    errors.city = 'Use letters only.';
  }
  if (values.state && !isValidState(values.state)) {
    errors.state = 'Choose a state from the list.';
  }
  if (trimmed('zip') && !isValidZip(values.zip)) {
    errors.zip = 'Enter a 5-digit ZIP code.';
  }

  return errors;
};

/* ------------------------------------------------------------------------ */

const FieldError = ({ id, message }) =>
  message ? (
    <div id={id} className="invalid-feedback d-block" role="alert">
      {message}
    </div>
  ) : null;

FieldError.propTypes = {
  id: PropTypes.string.isRequired,
  message: PropTypes.string
};

const MymRegistrationForm = () => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [touched, setTouched] = useState({});
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [showAddress, setShowAddress] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitted, setSubmitted] = useState(false);

  // Email-domain check: idle | checking | client | other | error.
  const [domainCheck, setDomainCheck] = useState({
    domain: '',
    status: 'idle'
  });

  const submitReq = useRequestState();
  const submitErrorRef = useRef(null);
  const domainResultsRef = useRef({});
  const { run: runSubmit } = submitReq;

  const emailValue = form.email.trim();
  const emailDomain = getEmailDomain(emailValue);

  /* ---------------------------- email domain check --------------------------- */

  // One call per domain; failures aren't cached so a later try can succeed.
  const runDomainCheck = useCallback(async email => {
    const domain = getEmailDomain(email);
    const known = domainResultsRef.current[domain];
    if (known) {
      return known;
    }
    try {
      // needs_organization: false → a client's email, no extra field.
      const needsOrganizationName = await checkRegistrationDomain(email);
      const status = needsOrganizationName ? 'other' : 'client';
      domainResultsRef.current[domain] = status;
      return status;
    } catch (error) {
      return 'error';
    }
  }, []);

  useEffect(() => {
    if (!emailDomain) {
      setDomainCheck({ domain: '', status: 'idle' });
      return undefined;
    }
    const known = domainResultsRef.current[emailDomain];
    if (known) {
      setDomainCheck({ domain: emailDomain, status: known });
      return undefined;
    }
    let cancelled = false;
    setDomainCheck({ domain: emailDomain, status: 'checking' });
    const timer = setTimeout(async () => {
      const status = await runDomainCheck(emailValue);
      if (!cancelled) {
        setDomainCheck({ domain: emailDomain, status });
      }
    }, 600);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [emailDomain, emailValue, runDomainCheck]);

  const domainStatus =
    domainCheck.domain === emailDomain ? domainCheck.status : 'idle';
  const needsOrganization = ASK_ORGANIZATION.includes(domainStatus);

  /* ------------------------------- validation ------------------------------ */

  const errors = useMemo(
    () => validate(form, { needsOrganization }),
    [form, needsOrganization]
  );

  const visibleError = name =>
    touched[name] || submitAttempted ? errors[name] : '';

  const markTouched = name =>
    setTouched(prev => (prev[name] ? prev : { ...prev, [name]: true }));

  const aboutDone =
    ['first_name', 'last_name', 'email', 'phone', 'organization_name'].every(
      name => !errors[name]
    ) && domainStatus !== 'checking';
  const addressFilled = ADDRESS_FIELDS.some(name => String(form[name]).trim());
  const addressDone =
    addressFilled && ADDRESS_FIELDS.every(name => !errors[name]);

  /* -------------------------------- handlers ------------------------------- */

  const handleFieldChange = e => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    setSubmitError('');
  };

  const handleBlur = e => markTouched(e.target.name);

  const focusField = name => {
    const target = document.getElementById(`reg-${name}`);
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      target.focus({ preventScroll: true });
    }
  };

  const focusFirstError = currentErrors => {
    const first = FIELD_ORDER.find(name => currentErrors[name]);
    if (!first) {
      return;
    }
    if (ADDRESS_FIELDS.includes(first) && !showAddress) {
      setShowAddress(true);
      setTimeout(() => focusField(first), 380);
    } else {
      focusField(first);
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (submitReq.isLoading) {
      return;
    }
    setSubmitAttempted(true);
    setSubmitError('');

    // The domain check may still be running (or never ran): settle it first,
    // so we know whether the organization name is needed.
    let askOrganization = needsOrganization;
    if (emailDomain && !['client', 'other'].includes(domainStatus)) {
      const status = await runDomainCheck(emailValue);
      setDomainCheck({ domain: emailDomain, status });
      askOrganization = ASK_ORGANIZATION.includes(status);
    }

    const currentErrors = validate(form, {
      needsOrganization: askOrganization
    });
    if (Object.keys(currentErrors).length > 0) {
      // Let the organization field appear before focusing it.
      setTimeout(() => focusFirstError(currentErrors), 60);
      return;
    }

    const payload = {
      first_name: form.first_name.trim(),
      last_name: form.last_name.trim(),
      email: form.email.trim(),
      phone: digitsOnly(form.phone),
      address: form.address.trim(),
      address2: form.address2.trim(),
      city: form.city.trim(),
      state: form.state,
      zip: form.zip.trim(),
      ...(askOrganization
        ? { organization_name: form.organization_name.trim() }
        : {}),
      website: form.website
    };

    const result = await runSubmit(() => submitRegistration(payload));
    if (result.stale) {
      return;
    }
    const response = result.ok ? result.data : null;

    if (response?.ok) {
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setSubmitError(
      response?.message ||
        getApiErrorMessage(result.error, NETWORK_ERROR_MESSAGE)
    );
    setTimeout(
      () =>
        submitErrorRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        }),
      50
    );
  };

  /* --------------------------------- render -------------------------------- */

  const renderTextField = ({
    name,
    label,
    icon,
    type = 'text',
    placeholder,
    autoComplete,
    required = false,
    optional = false,
    hint,
    maxLength,
    inputMode,
    onChange = handleFieldChange
  }) => {
    const error = visibleError(name);
    return (
      <Form.Group>
        <Form.Label htmlFor={`reg-${name}`}>
          {label}
          {required && <span className="mym-req">*</span>}
          {optional && <span className="mym-optional">(optional)</span>}
        </Form.Label>
        <div
          className={classNames('mym-field', {
            'is-invalid': !!error,
            'is-valid': !error && touched[name] && String(form[name]).trim()
          })}
        >
          <FontAwesomeIcon icon={icon} className="mym-field-icon" />
          <Form.Control
            id={`reg-${name}`}
            name={name}
            type={type}
            value={form[name]}
            placeholder={placeholder}
            autoComplete={autoComplete}
            maxLength={maxLength}
            inputMode={inputMode}
            onChange={onChange}
            onBlur={handleBlur}
            isInvalid={!!error}
            aria-describedby={error ? `reg-${name}-error` : undefined}
          />
        </div>
        <FieldError id={`reg-${name}-error`} message={error} />
        {!error && hint}
      </Form.Group>
    );
  };

  const emailHint = (() => {
    if (domainStatus === 'checking') {
      return (
        <div className="mym-field-hint">
          <Spinner animation="border" size="sm" /> Checking your email…
        </div>
      );
    }
    // if (domainStatus === 'client') {
    //   return (
    //     <div className="mym-field-hint is-success">
    //       <FontAwesomeIcon icon={faCheckCircle} /> We recognize your
    //       organization's email.
    //     </div>
    //   );
    // }
    return (
      <div className="mym-field-hint">
        We'll email you here when your access is ready.
      </div>
    );
  })();

  const sectionIcon = (done, icon, tone) => (
    <ToneIcon
      icon={done ? faCheck : icon}
      tone={done ? 'success' : tone}
      className={classNames({ 'is-done': done })}
    />
  );

  const body = submitted ? (
    <RegistrationSuccessPanel />
  ) : (
    <Form noValidate onSubmit={handleSubmit}>
      <div className="mym-reg-header">
        <h2 className="mym-reg-title">Request access</h2>
        <p className="mym-reg-lead">
          Your agency administrator reviews every request.
        </p>
      </div>

      {/* 1 — About you */}
      <section className="mym-reg-section" aria-labelledby="reg-about-title">
        <div className="mym-reg-section-head">
          {sectionIcon(aboutDone, faUser, 'primary')}
          <h3 id="reg-about-title" className="mym-reg-section-title">
            About you
          </h3>
        </div>
        <Row className="g-3 mb-3">
          <Col sm={6}>
            {renderTextField({
              name: 'first_name',
              label: 'First name',
              icon: faUser,
              placeholder: 'Jane',
              autoComplete: 'given-name',
              required: true
            })}
          </Col>
          <Col sm={6}>
            {renderTextField({
              name: 'last_name',
              label: 'Last name',
              icon: faUser,
              placeholder: 'Doe',
              autoComplete: 'family-name',
              required: true
            })}
          </Col>
          <Col sm={6}>
            {renderTextField({
              name: 'email',
              label: 'Work email',
              icon: faEnvelope,
              type: 'email',
              placeholder: 'jane.doe@example.org',
              autoComplete: 'email',
              required: true,
              hint: emailHint
            })}
          </Col>
          <Col sm={6}>
            <Form.Group>
              <Form.Label htmlFor="reg-phone">
                Phone<span className="mym-req">*</span>
              </Form.Label>
              <div
                className={classNames('mym-field', {
                  'is-invalid': !!visibleError('phone'),
                  'is-valid':
                    !visibleError('phone') && touched.phone && form.phone
                })}
              >
                <FontAwesomeIcon icon={faPhoneAlt} className="mym-field-icon" />
                <MaskedInput
                  id="reg-phone"
                  name="phone"
                  mask={PHONE_MASK}
                  guide={false}
                  inputMode="tel"
                  autoComplete="tel-national"
                  placeholder="(555) 123-4567"
                  className={classNames('form-control', {
                    'is-invalid': !!visibleError('phone')
                  })}
                  value={form.phone}
                  onChange={handleFieldChange}
                  onBlur={handleBlur}
                  aria-invalid={!!visibleError('phone')}
                  aria-describedby={
                    visibleError('phone') ? 'reg-phone-error' : undefined
                  }
                />
              </div>
              <FieldError
                id="reg-phone-error"
                message={visibleError('phone')}
              />
            </Form.Group>
          </Col>
        </Row>

        {/* Only when the email isn't from one of our client organizations. */}
        <Collapse in={needsOrganization}>
          <div>
            <div className="pb-3">
              {renderTextField({
                name: 'organization_name',
                label: 'Agency name',
                icon: faSitemap,
                placeholder: 'e.g., Bright Futures Care',
                autoComplete: 'organization',
                maxLength: ORGANIZATION_NAME_MAX,
                required: true,
                hint: (
                  <div className="mym-field-hint">
                    {domainStatus === 'error'
                      ? "We couldn't check your email right now. Enter your agency's name."
                      : "We couldn't find your agency from your email. Enter its name."}
                  </div>
                )
              })}
            </div>
          </div>
        </Collapse>
      </section>

      {/* 2 — Work address (optional). Nothing is mailed, so not "mailing". */}
      <section className="mym-reg-section" aria-labelledby="reg-address-title">
        <button
          type="button"
          className="mym-address-toggle mb-3"
          onClick={() => setShowAddress(open => !open)}
          aria-expanded={showAddress}
          aria-controls="reg-address-fields"
        >
          {sectionIcon(addressDone, faMapMarkedAlt, 'warning')}
          <span
            id="reg-address-title"
            className="mym-reg-section-title d-block"
          >
            Work address <span className="mym-optional">(optional)</span>
          </span>
          <FontAwesomeIcon icon={faChevronDown} className="mym-chevron" />
        </button>
        <Collapse in={showAddress}>
          <div id="reg-address-fields">
            <Row className="g-3 pb-3">
              <Col sm={6}>
                {renderTextField({
                  name: 'address',
                  label: 'Street address',
                  icon: faRoad,
                  placeholder: '123 Main St',
                  autoComplete: 'address-line1'
                })}
              </Col>
              <Col sm={6}>
                {renderTextField({
                  name: 'address2',
                  label: 'Apt, suite, unit',
                  icon: faBuilding,
                  placeholder: 'Suite 200',
                  autoComplete: 'address-line2'
                })}
              </Col>
              <Col sm={5}>
                {renderTextField({
                  name: 'city',
                  label: 'City',
                  icon: faCity,
                  placeholder: 'Boston',
                  autoComplete: 'address-level2'
                })}
              </Col>
              <Col sm={4}>
                <Form.Group>
                  <Form.Label htmlFor="reg-state">State</Form.Label>
                  <div
                    className={classNames('mym-field', {
                      'is-invalid': !!visibleError('state')
                    })}
                  >
                    <FontAwesomeIcon
                      icon={faMapMarkerAlt}
                      className="mym-field-icon"
                    />
                    <Form.Select
                      id="reg-state"
                      name="state"
                      value={form.state}
                      autoComplete="address-level1"
                      onChange={handleFieldChange}
                      onBlur={handleBlur}
                      isInvalid={!!visibleError('state')}
                    >
                      {STATE_OPTIONS.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.value ? option.key : 'State'}
                        </option>
                      ))}
                    </Form.Select>
                  </div>
                  <FieldError
                    id="reg-state-error"
                    message={visibleError('state')}
                  />
                </Form.Group>
              </Col>
              <Col sm={3}>
                {renderTextField({
                  name: 'zip',
                  label: 'ZIP',
                  icon: faHashtag,
                  placeholder: '02110',
                  autoComplete: 'postal-code',
                  inputMode: 'numeric',
                  maxLength: 5,
                  onChange: e =>
                    handleFieldChange({
                      target: {
                        name: 'zip',
                        value: digitsOnly(e.target.value).slice(0, 5)
                      }
                    })
                })}
              </Col>
            </Row>
          </div>
        </Collapse>
      </section>

      {/* Honeypot (backend R6.2): off-screen, never focusable, never shown.
          Off-screen positioning, NOT display:none — some bots skip hidden
          inputs. A filled value is silently dropped by the server. */}
      <Form.Control
        name="website"
        value={form.website}
        onChange={handleFieldChange}
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        style={{ position: 'absolute', left: '-9999px' }}
      />

      {submitError && (
        <div ref={submitErrorRef}>
          <IconAlert variant="danger" className="mt-3 mb-0">
            <p className="mb-0 fw-semibold">{submitError}</p>
          </IconAlert>
        </div>
      )}

      <div className="d-grid mt-4">
        <Button
          type="submit"
          className="mym-btn-primary"
          disabled={submitReq.isLoading}
        >
          {submitReq.isLoading ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Sending request…
            </>
          ) : (
            <>
              <FontAwesomeIcon icon={faPaperPlane} className="me-2" />
              Submit request
            </>
          )}
        </Button>
      </div>

      <p className="text-center fs-10 text-600 mt-3 mb-0">
        <FontAwesomeIcon icon={faUserTag} className="me-1 text-500" />
        Already have an account? <Link to="/">Sign in</Link>
      </p>
    </Form>
  );

  return (
    <RegistrationLayout
      title={
        <>
          Medication management
          <br />
          for your agency.
        </>
      }
      hero={<RegisterHeroIllustration />}
      features={PRODUCT_FEATURES}
    >
      {body}
    </RegistrationLayout>
  );
};

export default MymRegistrationForm;
